<script lang="ts">
	import Section from "#lib/web-ui/primitives/section.svelte";
	import Skeleton from "#lib/web-ui/ui/skeleton/skeleton.svelte";
	import { getTemplateContext } from "../template-context.svelte";
	import { page } from "$app/state";

	const templateContext = getTemplateContext();

	let {
		tag = null
	}: {
		tag?: string | null;
	} = $props();

	const allBlogPosts = templateContext.blogPosts.posts;
	const tags = templateContext.blogPosts.tags;
	const unavailable = templateContext.blogPosts.unavailable ?? false;

	const postsPerPage = 10;

	// Get current page from URL params
	const currentPage = $derived(Number.parseInt(page.url.searchParams.get("page") || "1"));

	// Filter posts by tag if specified
	const filteredPosts = $derived(
		tag ? allBlogPosts.filter((post) => post.tags.some((t) => t.slug === tag)) : allBlogPosts
	);

	// Calculate pagination
	const totalPages = $derived(Math.ceil(filteredPosts.length / postsPerPage));
	const startIndex = $derived((currentPage - 1) * postsPerPage);
	const endIndex = $derived(startIndex + postsPerPage);

	// Get posts for current page
	const paginatedPosts = $derived(filteredPosts.slice(startIndex, endIndex));
</script>

<svelte:head>
	<title>{tag ? `${tag} - ` : ""}Blog | {templateContext.siteName}</title>
	<meta
		name="description"
		content="Stay updated with the latest news, tips, and insights from {templateContext.siteName}."
	/>
	<link rel="canonical" href="{templateContext.siteUrl}blog" />
	<meta property="og:title" content="Blog | {templateContext.siteName}" />
	<meta
		property="og:description"
		content="Stay updated with the latest news, tips, and insights from {templateContext.siteName}."
	/>
	<meta property="og:url" content="{templateContext.siteUrl}blog" />
	<meta property="og:type" content="website" />
</svelte:head>

<Section>
	<div class="grid grid-cols-1 gap-8 lg:grid-cols-4">
		<!-- Header -->
		<header class="mb-8 lg:col-span-4">
			<h1 class="text-foreground mb-2 text-4xl font-bold">Blog</h1>
			<p class="text-muted-foreground text-lg">Stories, insights, and updates</p>
		</header>

		<!-- Tags Sidebar -->
		<aside class="lg:sticky lg:top-8 lg:col-span-1 lg:h-fit">
			<h3 class="text-foreground mb-4 text-sm font-bold tracking-wide uppercase">
				Filter by Tag
			</h3>
			<div class="flex flex-col gap-2">
				<a
					href="/blog"
					class={`rounded border px-4 py-2 transition-all ${
						!tag
							? "bg-primary border-primary text-white"
							: "border-border bg-muted text-foreground hover:bg-accent"
					}`}
				>
					All Posts
				</a>
				{#each tags as tagItem}
					<a
						href="/blog?tag={tagItem.slug}"
						class={`flex items-center justify-between rounded border px-4 py-2 text-sm transition-all ${
							tag === tagItem.slug
								? "bg-primary border-primary text-white"
								: "border-border bg-muted text-foreground hover:bg-accent"
						}`}
					>
						{tagItem.name}
						<span class="text-xs opacity-70">({tagItem.post_count})</span>
					</a>
				{/each}
			</div>
		</aside>

		<!-- Posts Container -->
		<main class="flex flex-col gap-8 lg:col-span-3">
			{#if paginatedPosts.length === 0}
				{#if unavailable}
					<div class="space-y-8">
						{#each Array(3) as _}
							<article
								class="bg-card grid grid-cols-1 gap-6 rounded-lg p-6 shadow-sm md:grid-cols-4"
							>
								<Skeleton class="h-48 w-full rounded md:col-span-1" />
								<div class="md:col-span-3">
									<Skeleton class="mb-3 h-7 w-3/4" />
									<Skeleton class="mb-3 h-4 w-32" />
									<Skeleton class="mb-2 h-4 w-full" />
									<Skeleton class="h-4 w-2/3" />
								</div>
							</article>
						{/each}
					</div>
					<p class="text-muted-foreground py-4 text-center">
						Our blog is temporarily unavailable. Please check back soon.
					</p>
				{:else}
					<p class="text-muted-foreground py-8 text-center">No posts found.</p>
				{/if}
			{:else}
				<div class="space-y-8">
					{#each paginatedPosts as post (post.slug)}
						{@const postPublicTags = post.tags}
						<article
							class="bg-card grid grid-cols-1 gap-6 rounded-lg p-6 shadow-sm transition-shadow hover:shadow-md md:grid-cols-4"
						>
							{#if post.featureImage}
								<a
									href="/blog/{post.slug}"
									class="overflow-hidden rounded md:col-span-1"
								>
									<img
										src={post.featureImage}
										alt={post.title}
										class="h-48 w-full object-cover transition-transform hover:scale-105"
										width="400"
										height="192"
										loading="lazy"
									/>
								</a>
							{/if}

							<div class={post.featureImage ? "md:col-span-3" : "md:col-span-4"}>
								<h2 class="text-foreground mb-1 text-2xl font-bold">
									<a
										href="/blog/{post.slug}"
										class="hover:text-primary transition-colors"
									>
										{post.title}
									</a>
								</h2>

								<time
									datetime={post.publishedAt}
									class="text-muted-foreground mb-3 block text-sm"
								>
									{new Date(post.publishedAt).toLocaleDateString("en-US", {
										year: "numeric",
										month: "long",
										day: "numeric"
									})}
								</time>

								<p class="text-muted-foreground mb-4 leading-relaxed">
									{post.excerpt}
								</p>

								<div class="flex flex-wrap items-center gap-2">
									{#if postPublicTags.length > 0}
										{#each postPublicTags as tag}
											<a
												href="/blog?tag={tag.slug}"
												class="bg-muted text-foreground hover:bg-accent inline-block rounded-full px-3 py-1 text-sm transition-colors"
											>
												#{tag.name}
											</a>
										{/each}
									{/if}

									<a
										href="/blog/{post.slug}"
										class="text-primary hover:text-primary/80 ml-auto font-semibold transition-colors"
									>
										Read More →
									</a>
								</div>
							</div>
						</article>
					{/each}
				</div>

				<!-- Pagination -->
				{#if totalPages > 1}
					<nav class="mt-8 flex items-center justify-center gap-8 p-8">
						{#if currentPage > 1}
							<a
								href="/blog?page={currentPage - 1}{tag ? `&tag=${tag}` : ''}"
								class="bg-primary hover:bg-primary/80 rounded px-4 py-2 text-white transition-colors"
							>
								← Previous
							</a>
						{/if}

						<div class="text-muted-foreground text-sm">
							Page {currentPage} of {totalPages}
						</div>

						{#if currentPage < totalPages}
							<a
								href="/blog?page={currentPage + 1}{tag ? `&tag=${tag}` : ''}"
								class="bg-primary hover:bg-primary/80 rounded px-4 py-2 text-white transition-colors"
							>
								Next →
							</a>
						{/if}
					</nav>
				{/if}
			{/if}
		</main>
	</div>
</Section>
